import React, { useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../api';
// import BusinessLocationAutocomplete from '../../components/masterdata/BusinessLocationAutocomplete';
import TomSelect from '../../components/common/TomSelect';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import FailModal from '../../components/common/FailModal';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import { usePagination } from '../../hooks/usePagination';
import { useAuth } from '../../contexts/AuthProvider';

const Companies = () => {
  // Use global auth context with capabilities
  const { user, can, capabilitiesLoading } = useAuth();

  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);
  
  // Helper function to check companies-specific capabilities
  const canCompanies = (capabilityKey) => can(capabilityKey, 'companies');

  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  const [showModal, setShowModal] = useState(false);

  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Applied term: only used after clicking Search or pressing Enter
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  // Company size map and options based on provided ranges
  const companySizeMap = {
    "1": "1 - 5",
    "2": "6 - 10",
    "3": "11 - 15",
    "4": "16 - 25",
    "5": "26 - 45",
    "6": "46 - 65",
    "7": "66 - 85",
    "8": "86 - 125",
    "9": "126 - 175",
    "10": "176 - 275",
    "11": "276 - 425",
    "12": "426 - 625",
    "13": "626 - 875",
    "14": "876 - 1175",
    "15": "1176 - 1550",
    "16": "1551 - 2025",
    "17": "2026 - 2675",
    "18": "2676 - 3450",
    "19": "3451 - 4350",
    "20": "4351 - 5450",
    "21": "5451 - 6800",
    "22": "6801 - 8500",
    "23": "8501 - 10700",
    "24": "> 10700",
    "-": "-"
  };

  const companySizeOptions = Object.entries(companySizeMap)
    .sort(([idA, nameA], [idB, nameB]) => {
      // Handle the "-" case to put it at the end
      if (idA === "-") return 1;
      if (idB === "-") return -1;
      
      // Sort by the numeric ID (key) for proper ascending order
      const numA = parseInt(idA, 10);
      const numB = parseInt(idB, 10);
      
      // If both are valid numbers, sort numerically
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      
      // Fallback to string comparison for any edge cases
      return idA.localeCompare(idB);
    })
    .map(([id, name]) => ({ id, name }));

  const navigate = useNavigate();
  const location = useLocation();
  // State for managing form submission status
  const [submitted, setSubmitted] = useState(false);

  // Base path is now always root
  const basePath = '';

  // Pagination
  const pagination = usePagination(10);

  // Manual search: update input only; fetch on button click or Enter
  const handleSearchInputChange = (value) => {
    setSearch(value);
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    // Apply the current input as the active search term
    setAppliedSearchTerm(searchTerm);
    // Reset to first page; fetching will be triggered by effect
    pagination.setPage(0);
  };

  const {
    selectedIds,
    setSelectedIds,
    handleCheckboxClick,
    handleSelectAll
  } = useSelection();

  const [partnerTypes, setPartnerTypes] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [taggingOptions, setTaggingOptions] = useState([]);
  const getIdrCurrencyId = () => {
    const idr = currencies.find(c => c.currencyName === 'Indonesian Rupiah' || c.currencyCode === 'IDR');
    return idr ? idr.id : '';
  };
  const [loading, setLoading] = useState(false);

  // State for managing form data
  const [formData, setFormData] = useState({
    businessPartnerType: '',
    companyName: '',
    sector: '',
    companySize: '',
    branchId: '',
    website: '',
    companyEmail: '',
    phone: '',
    businessLocation: '',
    address: '',
    country: '',
    state: '',
    city: '',
    postalCode: '',
    npwp: '',
    defaultCurrencyId: '',
    taggingIds: [],
    contacts: [{
      firstName: '',
      lastName: '',
      email: '',
      position: '',
      phone: '',
      partnerType: '',
      businessPartnerType: '',
      title: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      isPrimary: true,
      allowLogin: false,
      password: '',
      confirmPassword: '',
      generatePassword: false
    }],
    entityType: ''
  });

  // State for tracking validation errors
  const [errors, setErrors] = useState({
    contacts: [{}]
  });

  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Add state for generate password option
  const [generatePassword, setGeneratePassword] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [modalKey, setModalKey] = useState(0); // Key to force TomSelect re-initialization

  // Add new state for modals
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  // Add state for duplicate company name validation
  const [duplicateCompanies, setDuplicateCompanies] = useState([]);
  const [similarCompanies, setSimilarCompanies] = useState([]);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  // Function to handle select changes (TomSelect)
  const handleSelectChange = (name, value) => {
    // Multi-select (taggingIds) passes an array
    if (name === 'taggingIds') {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      const normalizedIds = arr.map(v => (v != null && v !== '') ? String(v) : null).filter(Boolean);
      setFormData(prev => ({ ...prev, [name]: normalizedIds }));
      if (normalizedIds.length > 0) setErrors(prev => ({ ...prev, [name]: undefined }));
      return;
    }
    // Normalize the value: convert empty strings, null, undefined to empty string
    // Handle both string and number values
    let normalizedValue = '';
    if (value !== null && value !== undefined && value !== '') {
      // Convert to string, but preserve the original if it's a valid value
      normalizedValue = String(value);
    }
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: normalizedValue
      };
      return updated;
    });

    // Clear error when a value is selected
    if (normalizedValue) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Validate the field if needed
    validateField(name, normalizedValue);
  };

  // Function to handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // For contact inputs
    if (name.startsWith('contacts[')) {
      const matches = name.match(/contacts\[(\d+)\]\.(.+)/);

      if (matches) {
        const index = parseInt(matches[1]);
        const field = matches[2];
        const updatedContacts = [...formData.contacts];
        updatedContacts[index][field] = value;

        setFormData((prev) => ({
          ...prev,
          contacts: updatedContacts
        }));

        validateContactField(index, field, value);
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Skip validation for optional fields (clear errors and don't validate)
    if (['country', 'state', 'city', 'postalCode', 'phone', 'website', 'companyEmail', 'npwp'].includes(name)) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
      return;
    }

    // Validate required fields
    const error = validateField(name, value, {
      required: true,
      type: getFieldType(name),
      minLength: getMinLength(name),
      maxLength: getMaxLength(name)
    });

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Helper function to determine field type
  const getFieldType = (fieldName) => {
    const typeMap = {
      email: 'email',
      phone: 'phone',
      website: 'website',
      password: 'password',
      confirmPassword: 'password',
      postalCode: 'postalCode'
    };
    return typeMap[fieldName] || 'text';
  };

  // Helper function to get min length for fields
  const getMinLength = (fieldName) => {
    const minLengthMap = {
      address: 5,
      password: 8
    };
    return minLengthMap[fieldName];
  };

  // Helper function to get max length for fields
  const getMaxLength = (fieldName) => {
    const maxLengthMap = {
      address: 500
    };
    return maxLengthMap[fieldName];
  };

  // Validate individual contact field
  const validateContactField = (index, fieldName, value) => {
    const newErrors = { ...errors };
    if (!newErrors.contacts[index]) {
      newErrors.contacts[index] = {};
    }

    let error = null;

    switch (fieldName) {
      case 'email':
        error = validateField(fieldName, value, {
          required: true,
          type: 'email'
        });
        break;
      case 'phone':
        error = validateField(fieldName, value, {
          required: true,
          type: 'phone'
        });
        break;
      case 'password':
        if (formData.contacts[index].allowLogin && !formData.contacts[index].generatePassword) {
          error = validateField(fieldName, value, {
            required: true,
            type: 'password',
            minLength: 8
          });
        }
        break;
      case 'confirmPassword':
        if (formData.contacts[index].allowLogin && !formData.contacts[index].generatePassword) {
          if (value !== formData.contacts[index].password) {
            error = 'Passwords do not match';
          }
        }
        break;
      default:
        error = validateField(fieldName, value, {
          required: true
        });
    }

    if (error) {
      newErrors.contacts[index][fieldName] = error;
    } else {
      delete newErrors.contacts[index][fieldName];
    }

    setErrors(newErrors);
    return !error;
  };

  // Function to check for duplicate and similar company names
  const checkCompanyNameDuplicates = async (companyName) => {
    if (!companyName || companyName.trim().length === 0) return;

    setIsCheckingDuplicates(true);
    try {
      // Check for exact duplicates and similar names
      const response = await api.get(`/md/companies/check-duplicates?name=${encodeURIComponent(companyName.trim())}`);
      const data = response.data;

      setDuplicateCompanies(data.exactDuplicates || []);
      setSimilarCompanies(data.similarCompanies || []);
    } catch (error) {
      console.error('Error checking for duplicate company names:', error);
      // If the endpoint doesn't exist yet, we'll handle validation on submit
      setDuplicateCompanies([]);
      setSimilarCompanies([]);
    } finally {
      setIsCheckingDuplicates(false);
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'companyName',
      'businessPartnerType',
      'sector',
      'companySize',
      'branchId',
      'address',
      'entityType'
    ];

    let isValid = true;

    // Check for exact duplicate company names (only if not editing or company name changed)
    if (duplicateCompanies.length > 0 && (!editingId || duplicateCompanies.some(comp => comp.id !== editingId))) {
      newErrors.companyName = 'A company with this name already exists. Please choose a different name.';
      isValid = false;
    }

    // Validate main form fields
    requiredFields.forEach(field => {
      // Normalize value: convert to string and handle null/undefined
      const fieldValue = formData[field];
      const normalizedValue = (fieldValue === null || fieldValue === undefined || fieldValue === '') ? '' : String(fieldValue);
      
      const error = validateField(field, normalizedValue, {
        required: true,
        type: getFieldType(field),
        minLength: getMinLength(field),
        maxLength: getMaxLength(field)
      });

      if (error) {
        newErrors[field] = error;
        isValid = false;
        console.log(`Validation error for ${field}:`, error, 'Raw Value:', formData[field], 'Normalized:', normalizedValue);
      }
    });

    // Special validation for Google Autocomplete fields
    // if (!formData.businessLocation?.trim() ||
    //   !formData.country?.trim() ||
    //   !formData.state?.trim() ||
    //   !formData.city?.trim()) {
    //   newErrors.businessLocation = 'Please select a valid business location from the suggestions';
    //   isValid = false;
    // } else {
    //   delete newErrors.businessLocation;
    //   delete newErrors.country;
    //   delete newErrors.state;
    //   delete newErrors.city;
    // }

    // Validate contacts
    newErrors.contacts = formData.contacts.map((contact, index) => {
      const contactErrors = {};
      const requiredContactFields = ['firstName', 'lastName', 'email', 'position', 'phone'];
      const contactFieldLabels = {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        phone: 'Phone Number',
        position: 'Position',
        password: 'Password',
        confirmPassword: 'Confirm Password'
      };
      // Validate required fields
      requiredContactFields.forEach(field => {
        // Use validateField directly to avoid side effects from validateContactField
        const fieldError = validateField(field, contact[field], {
          required: true,
          type: field === 'email' ? 'email' : field === 'phone' ? 'phone' : 'text'
        });
        if (fieldError) {
          contactErrors[field] = fieldError;
          isValid = false;
          console.log(`Validation error for contact[${index}].${field}:`, fieldError, 'Value:', contact[field]);
        }
      });

      // Validate login credentials if allowed
      if (contact.allowLogin && !contact.generatePassword) {
        const passwordError = validateField('password', contact.password, {
          required: true,
          type: 'password',
          minLength: 8
        });
        if (passwordError) {
          contactErrors.password = passwordError;
          isValid = false;
        }
        
        if (!contact.confirmPassword) {
          contactErrors.confirmPassword = 'Confirm password is required';
          isValid = false;
        } else if (contact.password !== contact.confirmPassword) {
          contactErrors.confirmPassword = 'Passwords do not match';
          isValid = false;
        }
      }

      return contactErrors;
    });

    console.log('Validation result:', isValid ? 'PASSED' : 'FAILED', 'Errors:', newErrors);
    setErrors(newErrors);
    return isValid;
  };

  const handleCreate = () => {
    if (!canCompanies('create')) {
      setFailMessage('You do not have permission to create companies.');
      setShowFailModal(true);
      return;
    }
    const defaultCurrencyId = getIdrCurrencyId();
    setFormData({
      businessPartnerType: '',
      companyName: '',
      sector: '',
      companySize: '',
      branchId: '',
      website: '',
      companyEmail: '',
      phone: '',
      businessLocation: '',
      address: '',
      country: '',
      state: '',
      city: '',
      postalCode: '',
      npwp: '',
      defaultCurrencyId: defaultCurrencyId,
      taggingIds: [],
      contacts: [{
        firstName: '',
        lastName: '',
        email: '',
        position: '',
        phone: '',
        partnerType: '',
        businessPartnerType: '',
        title: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        isPrimary: true,
        allowLogin: false,
        password: '',
        confirmPassword: '',
        generatePassword: false
      }],
      entityType: ''
    });
    setErrors({ contacts: [{}] });
    setSubmitted(false);
    setEditingId(null); // Reset ke mode "Create"
    setShowModal(true); // Munculkan modal
    setModalKey(prev => prev + 1); // Force TomSelect re-initialization
    const modal = new window.bootstrap.Modal(document.getElementById("modal-companies"), {
      backdrop: 'static',
      keyboard: true
    });
    modal.show();
  };

  const addContact = () => {
    const newContact = {
      firstName: '',
      lastName: '',
      email: '',
      position: '',
      phone: '',
      partnerType: '',
      businessPartnerType: '',
      title: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      isPrimary: false,
      allowLogin: false,
      password: '',
      confirmPassword: '',
      generatePassword: false
    };

    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, newContact]
    }));

    setErrors(prev => ({
      ...prev,
      contacts: [...prev.contacts, {}]
    }));
  };

  const handleContactLoginToggle = (index, checked) => {
    const updatedContacts = [...formData.contacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      allowLogin: checked,
      // Reset password fields when disabling login
      ...(checked === false && {
        password: '',
        confirmPassword: '',
        generatePassword: false
      })
    };

    setFormData(prev => ({
      ...prev,
      contacts: updatedContacts
    }));

    // Clear any password-related errors when disabling login
    if (!checked) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.contacts?.[index]) {
          delete newErrors.contacts[index].password;
          delete newErrors.contacts[index].confirmPassword;
        }
        return newErrors;
      });
    }
  };

  const handleGeneratePasswordToggle = (index, checked) => {
    const generateRandomPassword = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()';
      let password = '';
      for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return password;
    };

    const updatedContacts = [...formData.contacts];
    const generatedPassword = checked ? generateRandomPassword() : '';

    if (checked) {
      console.log(`Generated password for contact #${index + 1}: ${generatedPassword}`);
    }

    updatedContacts[index] = {
      ...updatedContacts[index],
      generatePassword: checked,
      password: generatedPassword,
      confirmPassword: generatedPassword
    };

    setFormData(prev => ({
      ...prev,
      contacts: updatedContacts
    }));

    // Clear any password-related errors when generating password
    if (checked) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.contacts?.[index]) {
          delete newErrors.contacts[index].password;
          delete newErrors.contacts[index].confirmPassword;
        }
        return newErrors;
      });
    }
  };

  // Function to remove a contact
  const removeContact = (index) => {
    if (formData.contacts.length > 1) {
      const updatedContacts = [...formData.contacts];
      updatedContacts.splice(index, 1);

      const updatedContactErrors = [...errors.contacts];
      updatedContactErrors.splice(index, 1);

      setFormData({
        ...formData,
        contacts: updatedContacts
      });

      setErrors({
        ...errors,
        contacts: updatedContactErrors
      });
    }
  };

  // Sample company data
  const [companies, setCompanies] = useState([]);

  // Function to handle row clicks
  const handleCompanyClick = (id) => {
    navigate(`/companies/${id}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    // Log formData before validation to debug
    console.log('FormData before validation:', JSON.stringify(formData, null, 2));
    console.log('Specific fields:', {
      businessPartnerType: formData.businessPartnerType,
      sector: formData.sector,
      companySize: formData.companySize,
      entityType: formData.entityType,
      companyName: formData.companyName,
      address: formData.address
    });

    const isValid = validateForm();

    if (!isValid) {
      // Show user feedback when validation fails
      setFailMessage('Please fix the validation errors before submitting the form.');
      setShowFailModal(true);
      // Scroll to first error field
      const firstErrorField = document.querySelector('.is-invalid');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.focus();
      }
      return;
    }

    setLoading(true);
    try {
      // Try to detect current user ID from localStorage/session/jwt
      const getCurrentUserId = () => {
        try {
          const storedUser = localStorage.getItem('user') || localStorage.getItem('authUser') || sessionStorage.getItem('user');
          if (storedUser) {
            const obj = JSON.parse(storedUser);
            if (obj && (obj.id || obj.userId)) return obj.id || obj.userId;
          }
        } catch { }
        try {
          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
          if (token && token.split('.').length === 3) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.id || payload.userId || payload.sub || null;
          }
        } catch { }
        return null;
      };

      const currentUserId = getCurrentUserId();

      const taggingIdsAsNumbers = (formData.taggingIds && Array.isArray(formData.taggingIds))
        ? formData.taggingIds.map(id => Number(id)).filter(n => !isNaN(n))
        : [];

      if (editingId) {
        const updatePayload = {
          ...formData,
          taggingIds: taggingIdsAsNumbers,
          ...(currentUserId ? { uuserId: currentUserId, uuser_id: currentUserId, uuser: { id: currentUserId } } : {})
        };
        await api.put(`/md/companies/${editingId}`, updatePayload);
        setSuccessMessage('Data has been successfully updated.');
      } else {
        const createPayload = {
          ...formData,
          taggingIds: taggingIdsAsNumbers,
          ...(currentUserId ? { cuserId: currentUserId, cuser_id: currentUserId, cuser: { id: currentUserId } } : {})
        };
        await api.post('/md/companies', createPayload);
        setSuccessMessage('Data has been successfully added.');
      }
      setEditingId(null);
      resetForm();
      await fetchCompanies(searchTerm);
      const modal = document.getElementById('modal-companies');
      const bootstrapModal = window.bootstrap?.Modal.getInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
        setTimeout(() => setShowSuccessModal(true), 300);
      } else {
        setShowSuccessModal(true);
      }
    } catch (error) {
      let errorMessage = 'Failed to save data.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid data provided.';
            technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 409:
            errorMessage = 'A company with this information already exists.';
            technicalDetails = error.response.data.message || 'Duplicate entry detected';
            break;
          case 500:
            errorMessage = 'Server encountered an error while saving.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while saving the data.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
    } finally {
      setLoading(false);
    }
  };


  const resetForm = () => {
    setFormData({
      businessPartnerType: '',
      companyName: '',
      sector: '',
      companySize: '',
      branchId: '',
      website: '',
      companyEmail: '',
      phone: '',
      businessLocation: '',
      address: '',
      country: '',
      state: '',
      city: '',
      postalCode: '',
      npwp: '',
      defaultCurrencyId: '',
      taggingIds: [],
      contacts: [{
        firstName: '',
        lastName: '',
        email: '',
        position: '',
        phone: '',
        partnerType: '',
        businessPartnerType: '',
        title: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        isPrimary: true,
        allowLogin: false,
        password: '',
        confirmPassword: '',
        generatePassword: false
      }],
      entityType: ''
    });
    setErrors({ contacts: [{}] });
    setSubmitted(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setGeneratePassword(false);
    // Clear duplicate checking states
    setDuplicateCompanies([]);
    setSimilarCompanies([]);
    setIsCheckingDuplicates(false);
  };

  // Add useEffect for modal handling
  useEffect(() => {
    const modal = document.getElementById('modal-companies');
    if (!modal) return;

    const handleModalHide = () => {
      // Reset form data when modal is hidden
      setFormData({
        businessPartnerType: '',
        companyName: '',
        sector: '',
        companySize: '',
        website: '',
        companyEmail: '',
        phone: '',
        businessLocation: '',
        address: '',
        country: '',
        state: '',
        city: '',
        postalCode: '',
        npwp: '',
        taggingIds: [],
        contacts: [{
          firstName: '',
          lastName: '',
          email: '',
          position: '',
          phone: '',
          partnerType: '',
          businessPartnerType: '',
          title: '',
          address: '',
          city: '',
          state: '',
          postalCode: '',
          isPrimary: true,
          allowLogin: false,
          password: '',
          confirmPassword: '',
          generatePassword: false
        }],
        entityType: ''
      });
      setErrors({ contacts: [{}] });
      setSubmitted(false);
      setEditingId(null);
      // Clear duplicate checking states
      setDuplicateCompanies([]);
      setSimilarCompanies([]);
      setIsCheckingDuplicates(false);
    };

    modal.addEventListener('hidden.bs.modal', handleModalHide);

    return () => {
      modal.removeEventListener('hidden.bs.modal', handleModalHide);
    };
  }, []); // No dependencies needed since we're not using TomSelect initialization here anymore

  const fetchCompanies = async (searchTerm = '') => {
    setLoading(true);
    try {
      // Build query parameters like the original implementation
      const queryParams = new URLSearchParams();
      queryParams.append('page', pagination.page);
      queryParams.append('size', pagination.size);
      if (searchTerm) queryParams.append('search', searchTerm);
      if (filterFromDate) queryParams.append('from', filterFromDate);
      if (filterToDate) queryParams.append('to', filterToDate);

      if (!canCompanies('view_global')) {
        if (canCompanies('view_department')) {
          if (user?.departmentId) queryParams.append('viewDepartmentId', user.departmentId);
        } else if (canCompanies('view_own')) {
          if (user?.id) queryParams.append('createdBy', user.id);
        }
      }

      const res = await api.get(`/md/companies-page?${queryParams.toString()}`);
      const content = res.data.content || res.data.data || [];
      setCompanies(content);
      const totalElements = res.data.totalElements || res.data.total || 0;
      const totalPagesFromApi = res.data.totalPages;
      const computedTotalPages = totalElements && pagination.size ? Math.ceil(totalElements / pagination.size) : 0;
      pagination.setTotalPages(typeof totalPagesFromApi === 'number' ? totalPagesFromApi : computedTotalPages);
      pagination.setTotal(totalElements);
      pagination.setCurrentPageItems(content.length);
    } catch (error) {
      let errorMessage = 'Failed to fetch companies.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view companies.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server encountered an error while fetching companies.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching companies.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (!canCompanies('edit')) {
      setFailMessage('You do not have permission to edit companies.');
      setShowFailModal(true);
      return;
    }
    if (selectedIds.length !== 1) {
      window.alert("Pilih tepat satu perusahaan untuk diedit.");
      return;
    }
    fetchCompanyDetail(selectedIds[0]); // Ambil data detail dari backend
  };

  const handleDelete = async () => {
    if (!canCompanies('delete')) {
      setFailMessage('You do not have permission to delete companies.');
      setShowFailModal(true);
      return;
    }
    if (selectedIds.length === 0) {
      setFailMessage('Please select at least one company to delete.\n\nTechnical Details: No items selected for deletion');
      setShowFailModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        await api.delete('/md/companies', {
          data: selectedIds
        });
        setSelectedIds([]);
        await fetchCompanies(searchTerm);
        setShowDeleteConfirmation(false);
        setSuccessMessage('Selected companies have been successfully deleted.');
        setShowSuccessModal(true);
      } catch (error) {
        let errorMessage = 'Failed to delete companies.';
        let technicalDetails = '';

        if (error.response) {
          switch (error.response.status) {
            case 403:
              errorMessage = 'You do not have permission to delete these items.';
              technicalDetails = 'Required role or permission is missing';
              break;
            case 404:
              errorMessage = 'One or more companies not found.';
              technicalDetails = 'Some items may have been already deleted';
              break;
            case 500:
              errorMessage = 'Server error while deleting items.';
              technicalDetails = error.response.data.message || 'Internal server error during deletion';
              break;
            default:
              errorMessage = 'An error occurred while deleting the items.';
              technicalDetails = `Server responded with status: ${error.response.status}`;
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server.';
          technicalDetails = 'Network error or server is not responding';
        } else {
          errorMessage = 'An unexpected error occurred during deletion.';
          technicalDetails = error.message;
        }

        setShowDeleteConfirmation(false);
        setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
        setShowFailModal(true);
      }
    });
    setShowDeleteConfirmation(true);
  };

  const fetchCompanyDetail = async (id) => {
    try {
      const res = await api.get(`/md/companies/${id}`);
      const data = res.data;

      const companyData = data.company;
      // ✅ FIX: Ensure contacts include their IDs for proper matching during update
      companyData.contacts = (data.contacts || []).map(contact => ({
        ...contact,
        id: contact.id, // Preserve contact ID for backend matching
      }));

      // ✅ FIX: Transform backend data to extract IDs for TomSelect fields
      // Handle businessPartnerType - extract ID if it's an object, or find by name if it's a string
      if (companyData.businessPartnerType) {
        if (typeof companyData.businessPartnerType === 'object') {
          companyData.businessPartnerType = companyData.businessPartnerType.id || companyData.businessPartnerType;
        } else if (typeof companyData.businessPartnerType === 'string' && partnerTypes.length > 0) {
          // If it's a string (name), try to find the matching ID
          const found = partnerTypes.find(pt => 
            pt.name === companyData.businessPartnerType || String(pt.id) === String(companyData.businessPartnerType)
          );
          if (found) {
            companyData.businessPartnerType = String(found.id);
          }
        }
      }
      // Ensure it's a string for TomSelect
      companyData.businessPartnerType = companyData.businessPartnerType != null ? String(companyData.businessPartnerType) : '';

      // Handle entityType - extract ID if it's an object, or find by entityType name if it's a string
      if (companyData.entityType) {
        if (typeof companyData.entityType === 'object') {
          companyData.entityType = companyData.entityType.id || companyData.entityType;
        } else if (typeof companyData.entityType === 'string' && entityTypes.length > 0) {
          // If it's a string (entityType name), try to find the matching ID
          const found = entityTypes.find(et => 
            et.entityType === companyData.entityType || String(et.id) === String(companyData.entityType)
          );
          if (found) {
            companyData.entityType = String(found.id);
          }
        }
      }
      // Ensure it's a string for TomSelect
      companyData.entityType = companyData.entityType != null ? String(companyData.entityType) : '';

      // Handle sector - extract ID if it's an object, or find by name if it's a string
      if (companyData.sector) {
        if (typeof companyData.sector === 'object') {
          companyData.sector = companyData.sector.id || companyData.sector;
        } else if (typeof companyData.sector === 'string' && sectors.length > 0) {
          // If it's a string (name), try to find the matching ID
          const found = sectors.find(s => 
            s.name === companyData.sector || String(s.id) === String(companyData.sector)
          );
          if (found) {
            companyData.sector = String(found.id);
          }
        }
      }
      // Ensure it's a string for TomSelect
      companyData.sector = companyData.sector != null ? String(companyData.sector) : '';

      // Handle companySize - ensure it matches the format (string ID)
      companyData.companySize = companyData.companySize != null ? String(companyData.companySize) : '';

      // Handle defaultCurrencyId - extract ID if it's an object, or find by currency name/code
      if (companyData.defaultCurrencyId) {
        if (typeof companyData.defaultCurrencyId === 'object') {
          companyData.defaultCurrencyId = companyData.defaultCurrencyId.id || companyData.defaultCurrencyId;
        } else if (typeof companyData.defaultCurrencyId === 'string' && currencies.length > 0) {
          // If it's a string, try to find the matching ID by currencyName or currencyCode
          const found = currencies.find(c => 
            c.currencyName === companyData.defaultCurrencyId || 
            c.currencyCode === companyData.defaultCurrencyId ||
            String(c.id) === String(companyData.defaultCurrencyId)
          );
          if (found) {
            companyData.defaultCurrencyId = String(found.id);
          }
        }
      }
      // Also check if it's stored as defaultCurrency or currency
      if (!companyData.defaultCurrencyId && companyData.defaultCurrency) {
        if (typeof companyData.defaultCurrency === 'object') {
          companyData.defaultCurrencyId = companyData.defaultCurrency.id;
        } else if (typeof companyData.defaultCurrency === 'string' && currencies.length > 0) {
          const found = currencies.find(c => 
            c.currencyName === companyData.defaultCurrency || 
            c.currencyCode === companyData.defaultCurrency
          );
          if (found) {
            companyData.defaultCurrencyId = String(found.id);
          }
        }
      }
      if (!companyData.defaultCurrencyId && companyData.currency) {
        if (typeof companyData.currency === 'object') {
          companyData.defaultCurrencyId = companyData.currency.id;
        } else if (typeof companyData.currency === 'string' && currencies.length > 0) {
          const found = currencies.find(c => 
            c.currencyName === companyData.currency || 
            c.currencyCode === companyData.currency
          );
          if (found) {
            companyData.defaultCurrencyId = String(found.id);
          }
        }
      }
      // Ensure it's a string for TomSelect
      companyData.defaultCurrencyId = companyData.defaultCurrencyId != null ? String(companyData.defaultCurrencyId) : '';

      // Handle taggings: company detail returns taggings array [{ id, name }, ...]
      if (companyData.taggings && Array.isArray(companyData.taggings)) {
        companyData.taggingIds = companyData.taggings.map(t => String(t.id));
      } else if (companyData.tagging && typeof companyData.tagging === 'string' && taggingOptions.length > 0) {
        // Fallback: single comma-separated string from list API
        const names = companyData.tagging.split(',').map(s => s.trim()).filter(Boolean);
        companyData.taggingIds = names.map(n => {
          const found = taggingOptions.find(opt => opt.name === n || String(opt.id) === n);
          return found ? String(found.id) : null;
        }).filter(Boolean);
      } else {
        companyData.taggingIds = [];
      }
      companyData.branchId = companyData.branchId != null ? String(companyData.branchId) : (companyData.branch?.id != null ? String(companyData.branch.id) : '');
      delete companyData.clientFlagging;
      delete companyData.clientFlaggingId;
      delete companyData.taggings;

      setFormData(companyData);
      setEditingId(id);
      setModalKey(prev => prev + 1); // Force TomSelect re-initialization

      const modal = new window.bootstrap.Modal(document.getElementById("modal-companies"), {
        backdrop: 'static',
        keyboard: true
      });
      modal.show();
    } catch (error) {
      console.error("Gagal mengambil data:", error);
      window.alert("Gagal mengambil data perusahaan.");
    }
  };

  const handleApplyFilter = () => {
    fetchCompanies(searchTerm); // Akan pakai tanggal dari state filter
  };

  const handleResetFilter = () => {
    setFilterFromDate('');
    setFilterToDate('');
    fetchCompanies(searchTerm); // Reload tanpa filter
  };

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [partnerTypeRes, entityTypeRes, sectorRes, currencyRes, taggingRes, branchesRes] = await Promise.all([
          api.get('/md/business-partner-type'),
          api.get('/md/entity-type'),
          api.get('/md/sector'),
          api.get('/crm/currencies'),
          api.get('/md/tagging', { params: { moduleKey: 'companies' } }),
          api.get('/branches')
        ]);
        setPartnerTypes(partnerTypeRes.data);
        setEntityTypes(entityTypeRes.data);
        setSectors(sectorRes.data);
        setCurrencies(currencyRes.data);
        setTaggingOptions(taggingRes.data || []);
        const branchData = branchesRes.data?.data ?? branchesRes.data ?? [];
        setBranches(Array.isArray(branchData) ? branchData : []);
        // Set default currency to Indonesian Rupiah for create mode
        const idr = (currencyRes.data || []).find(c => c.currencyName === 'Indonesian Rupiah' || c.currencyCode === 'IDR');
        if (idr && !editingId) {
          setFormData(prev => ({ ...prev, defaultCurrencyId: idr.id }));
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!capabilitiesLoading) {
      fetchCompanies(appliedSearchTerm);
    }
  }, [pagination.page, pagination.size, appliedSearchTerm, capabilitiesLoading]);

  // Check for duplicate company names when company name changes
  useEffect(() => {
    if (formData.companyName && formData.companyName.trim().length > 2) {
      const timeoutId = setTimeout(() => {
        checkCompanyNameDuplicates(formData.companyName);
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timeoutId);
    } else {
      setDuplicateCompanies([]);
      setSimilarCompanies([]);
    }
  }, [formData.companyName]);

  // Date formatter function for dd/mm/yyyy format
  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Define columns for GenericView table
  const columns = [
    {
      key: 'entityType',
      label: 'Entity Type',
      sortable: true
    },
    {
      key: 'businessPartnerType',
      label: 'Business Partner Type',
      sortable: true
    },
    {
      key: 'name',
      label: 'Company',
      sortable: true
    },
    {
      key: 'sector',
      label: 'Sector',
      sortable: true
    },
    {
      key: 'size',
      label: 'Size',
      sortable: true,
      render: (value) => companySizeMap[value] || value
    },
    {
      key: 'website',
      label: 'Website',
      sortable: true
    },
    {
      key: 'npwp',
      label: 'NPWP',
      sortable: true
    },
    {
      key: 'country',
      label: 'Country',
      sortable: true
    },
    {
      key: 'state',
      label: 'State',
      sortable: true
    },
    {
      key: 'city',
      label: 'City',
      sortable: true
    },
    {
      key: 'postalCode',
      label: 'Postal Code',
      sortable: true
    },
    {
      key: 'tagging',
      label: 'Tagging',
      sortable: true,
      render: (value) => {
        if (!value) return '-';
        // API returns "tagging" as comma-separated string from list
        if (typeof value === 'string') return value;
        if (typeof value === 'object' && value.name) return value.name;
        const option = taggingOptions.find(opt => String(opt.id) === String(value));
        return option ? option.name : value;
      }
    },
    {
      key: 'created',
      label: 'Created Date',
      sortable: true,
      type: 'date',
      render: (value) => formatDate(value)
    }
  ];

  return (
    <>
      {/* Page header */}
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">
                Companies
              </h2>
            </div>
            {/* Page title actions */}
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                {canCompanies('create') && (
                  <a href="#" className="btn btn-primary d-none d-sm-inline-block"
                    onClick={handleCreate}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                      strokeLinejoin="round" className="icon icon-2">
                      <path d="M12 5l0 14" />
                      <path d="M5 12l14 0" />
                    </svg>
                    Create
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        <div className="container-xl">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <GenericView
                  data={companies}
                  columns={columns}
                  isLoading={loading || capabilitiesLoading}
                  emptyStateMessage="No companies found"
                  checkboxSelection={true}
                  selectedRows={selectedIds}
                  onSelectRow={(itemId, isSelected) => {
                    if (isSelected) {
                      setSelectedIds([...selectedIds, itemId]);
                    } else {
                      setSelectedIds(selectedIds.filter(id => id !== itemId));
                    }
                  }}
                  onSelectAllRows={(itemIds) => {
                    if (Array.isArray(itemIds) && itemIds.length > 0) {
                      setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                    } else {
                      setSelectedIds([]);
                    }
                  }}
                  onEdit={canCompanies('edit') ? (item) => {
                    setSelectedIds([item.id]);
                    handleEdit();
                  } : undefined}
                  onDelete={canCompanies('delete') ? (item) => {
                    setSelectedIds([item.id]);
                    handleDelete();
                  } : undefined}
                  searchComponent={
                    <div className="d-flex gap-2">
                      <SearchInput
                        value={searchTerm}
                        onChange={handleSearchInputChange}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearchSubmit();
                          }
                        }}
                        placeholder="Search by name..."
                        uniqueId="companies-search"
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSearchSubmit}
                      >
                        Search
                      </button>
                      <a className="btn btn-icon" data-bs-toggle="offcanvas" href="#offcanvasEnd"
                        role="button" aria-controls="offcanvasEnd">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                          viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-adjustments">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M4 10a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                          <path d="M6 4v4" />
                          <path d="M6 12v8" />
                          <path d="M10 16a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                          <path d="M12 4v10" />
                          <path d="M12 18v2" />
                          <path d="M16 7a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                          <path d="M18 4v1" />
                          <path d="M18 9v11" />
                        </svg>
                      </a>
                    </div>
                  }
                  actionButtons={
                    <div className="d-flex gap-2">
                      {(canCompanies('edit') || canCompanies('delete')) && (
                        <div ref={actionsContainerRef} className="dropdown">
                          <a
                            ref={actionsToggleRef}
                            href="#"
                            className="btn dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded={isActionsOpen}
                            onClick={(e) => {
                              e.preventDefault();
                              const inst = actionsDropdownInstanceRef.current;
                              if (inst?.toggle) inst.toggle();
                              else setIsActionsOpen(prev => !prev);
                            }}
                          >Actions</a>
                          <div className="dropdown-menu">
                            {canCompanies('edit') && (
                              <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleEdit(); }}
                                style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                Edit
                              </a>
                            )}
                            {canCompanies('delete') && (
                              <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleDelete(); }}
                                style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                Delete
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  }
                  pagination={{
                    currentPage: pagination.page + 1, // GenericView uses 1-based pages
                    totalRows: pagination.total,
                    rowsPerPage: pagination.size,
                    onPageChange: (page) => pagination.setPage(page - 1), // Convert back to 0-based
                    onRowsPerPageChange: (size) => {
                      pagination.setSize(size);
                      pagination.setPage(0);
                    }
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  uniqueId="companies-table"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New Company Modal */}
      <div className="modal modal-blur fade" id="modal-companies" tabIndex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="true">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <form onSubmit={handleSubmit} noValidate>
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? "Edit Company" : "New Company"}</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-lg-12">
                    <h5 className="modal-title">Company Information</h5>
                  </div>
                  <div className="col-lg-6">

                    <div className="mb-3">
                      <TomSelect
                        key={`businessPartnerType-${modalKey}`}
                        name="businessPartnerType"
                        value={formData.businessPartnerType === undefined || formData.businessPartnerType === null ? '' : String(formData.businessPartnerType)}
                        onChange={handleSelectChange}
                        options={partnerTypes}
                        placeholder="Select partner type"
                        label="Business Partner Type"
                        required={true}
                        error={errors.businessPartnerType}
                        valueField="id"
                        labelField="name"
                        searchField="name"
                        autoComplete="off"
                      />
                    </div>

                  </div>
                  <div className="col-lg-8">
                  </div>
                  <div className="col-lg-6">

                    <div className="mb-3">
                      <TomSelect
                        key={`entityType-${modalKey}`}
                        name="entityType"
                        value={formData.entityType === undefined || formData.entityType === null ? '' : String(formData.entityType)}
                        onChange={handleSelectChange}
                        options={entityTypes}
                        placeholder="Select entity type"
                        label="Entity Type"
                        required={true}
                        error={errors.entityType}
                        valueField="id"
                        labelField="entityType"
                        searchField="entityType"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Company Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className={`form-control ${errors.companyName ? 'is-invalid' : ''}`}
                        name="companyName"
                        id="companyInput"
                        placeholder="Enter company name"
                        value={formData.companyName || ''}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      {errors.companyName && <div className="invalid-feedback">{errors.companyName}</div>}
{/* Duplicate company warning */}
{duplicateCompanies.length > 0 && !errors.companyName && (
  <small className="text-danger d-block mt-1">
    A company with this exact name already exists. Please choose a different name.
  </small>
)}

{/* Similar company warning */}
{similarCompanies.length > 0 && duplicateCompanies.length === 0 && !errors.companyName && (
  <small className="text-warning d-block mt-1">
    Similar company names found:{" "}
    {similarCompanies.slice(0, 3).map((company, i) => (
      <span key={i}>
        <strong>{company.name}</strong>
      </span>
    ))}
    {similarCompanies.length > 3 && (
      <span> and {similarCompanies.length - 3} more</span>
    )}
    . Please verify it is not a duplicate entry.
  </small>
)}

{/* Loading */}
{isCheckingDuplicates && formData.companyName?.trim().length > 2 && (
  <small className="text-muted d-block mt-1">
    Checking for duplicate company names...
  </small>
)}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <TomSelect
                        key={`sector-${modalKey}`}
                        name="sector"
                        value={formData.sector === undefined || formData.sector === null ? '' : String(formData.sector)}
                        onChange={handleSelectChange}
                        options={sectors}
                        placeholder="Select sector"
                        label="Sector"
                        required={true}
                        error={errors.sector}
                        valueField="id"
                        labelField="name"
                        searchField="name"
                        autoComplete="off"
                      />
                    </div>

                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <TomSelect
                        key={`companySize-${modalKey}`}
                        name="companySize"
                        value={formData.companySize === undefined || formData.companySize === null ? '' : String(formData.companySize)}
                        onChange={handleSelectChange}
                        options={companySizeOptions}
                        placeholder="Select size"
                        label="Company Size"
                        required={true}
                        error={errors.companySize}
                        autoComplete="off"
                        sortField={null}
                      />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="mb-3">
                      <TomSelect
                        key={`branchId-${modalKey}`}
                        name="branchId"
                        value={formData.branchId === undefined || formData.branchId === null ? '' : String(formData.branchId)}
                        onChange={handleSelectChange}
                        options={branches}
                        placeholder="Select branch"
                        label="Branch"
                        required={true}
                        error={errors.branchId}
                        valueField="id"
                        labelField="name"
                        searchField="name"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Website</label>
                      <input
                        type="text"
                        className={`form-control ${errors.website ? 'is-invalid' : ''}`}
                        name="website"
                        id="websiteInput"
                        placeholder="Enter website URL"
                        value={formData.website || ''}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      <div className="form-hint">Optional</div>
                      {errors.website && <div className="invalid-feedback">{errors.website}</div>}
                    </div>
                  </div>
                  {/* company email */}
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Company Email</label>
                      <input
                        type="email"
                        className={`form-control ${errors.companyEmail ? 'is-invalid' : ''}`}
                        name="companyEmail"
                        id="companyEmailInput"
                        placeholder="Enter company email"
                        value={formData.companyEmail || ''}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      <div className="form-hint">Optional</div>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Company Phone Number</label>
                      <input
                        type="tel"
                        className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                        name="phone"
                        id="phoneInput"
                        placeholder="Enter phone number"
                        value={formData.phone || ''}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      <div className="form-hint">Optional</div>
                      {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Company Tax Number/NPWP</label>
                      <input
                        type="text"
                        className={`form-control ${errors.npwp ? 'is-invalid' : ''}`}
                        name="npwp"
                        id="npwpInput"
                        placeholder="Enter company tax number/NPWP"
                        value={formData.npwp || ''}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  {/* ✅ MULTI-CURRENCY: Default Currency Selection */}
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <TomSelect
                        key={`defaultCurrencyId-${modalKey}`}
                        name="defaultCurrencyId"
                        value={formData.defaultCurrencyId === undefined || formData.defaultCurrencyId === null ? '' : String(formData.defaultCurrencyId)}
                        onChange={handleSelectChange}
                        options={currencies}
                        placeholder="Select default currency"
                        label="Default Currency"
                        required={false}
                        error={errors.defaultCurrencyId}
                        valueField="id"
                        labelField="currencyName"
                        searchField="currencyName"
                        autoComplete="off"
                      />
                      <div className="form-hint">Optional: Default currency for this company's proposals</div>
                    </div>
                  </div>

                  {/* Tagging (multi-select) */}
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <TomSelect
                        key={`taggingIds-${modalKey}`}
                        name="taggingIds"
                        value={Array.isArray(formData.taggingIds) ? formData.taggingIds : []}
                        onChange={handleSelectChange}
                        options={taggingOptions}
                        placeholder="Select tagging(s)"
                        label="Tagging"
                        required={false}
                        error={errors.taggingIds}
                        valueField="id"
                        labelField="name"
                        searchField="name"
                        autoComplete="off"
                        multiple={true}
                      />
                      <div className="form-hint">Optional: One or more tags for this company</div>
                    </div>
                  </div>

                  {/* <div className='col-lg-8'>
                    <BusinessLocationAutocomplete
                      formData={formData}
                      setFormData={setFormData}
                      errors={errors}
                    />
                  </div> */}

                  <div className="col-lg-12">
                    <label className="form-label">Address <span className="text-danger">*</span></label>
                  </div>
                  <div className="col-lg-8">
                    <div className="mb-3">
                      <textarea
                        className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                        name="address"
                        id="addressInput"
                        placeholder="Address"
                        value={formData.address || ''}
                        onChange={handleInputChange}
                        autoComplete="off"
                        rows="3"
                      />
                      {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <input
                        type="text"
                        className={`form-control ${errors.postalCode ? 'is-invalid' : ''}`}
                        name="postalCode"
                        id="postalInput"
                        placeholder="Postal Code"
                        value={formData.postalCode || ''}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      {errors.postalCode && <div className="invalid-feedback">{errors.postalCode}</div>}
                    </div>
                  </div>

                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                        name="country"
                        id="countryInput"
                        placeholder="Enter country"
                        value={formData.country || ''}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">State</label>
                      <input
                        type="text"
                        className={`form-control ${errors.state ? 'is-invalid' : ''}`}
                        name="state"
                        id="stateInput"
                        placeholder="Enter state"
                        value={formData.state || ''}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      {errors.state && <div className="invalid-feedback">{errors.state}</div>}
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                        name="city"
                        id="cityInput"
                        placeholder="Enter city"
                        value={formData.city || ''}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-body">
                <div className="row">
                  <div className="col-lg-12 d-flex justify-content-between align-items-center mb-3">
                    <h5 className="modal-title">Contact Person</h5>
                  </div>

                  {formData.contacts.map((contact, index) => (
                    <div key={index} className="col-12">
                      <div className="card mb-3">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h5 className="card-title mb-0">
                            {index === 0 ? 'Primary Contact' : `Additional Contact ${index}`}
                          </h5>
                          {index > 0 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => removeContact(index)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-lg-6">
                              <div className="mb-3">
                                <label className="form-label">First Name <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.contacts[index]?.firstName ? 'is-invalid' : ''}`}
                                  name={`contacts[${index}].firstName`}
                                  placeholder="Enter first name"
                                  value={contact.firstName || ''}
                                  onChange={handleInputChange}
                                  autoComplete="off"
                                />
                                {errors.contacts[index]?.firstName &&
                                  <div className="invalid-feedback">{errors.contacts[index].firstName}</div>
                                }
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="mb-3">
                                <label className="form-label">Last Name <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.contacts[index]?.lastName ? 'is-invalid' : ''}`}
                                  name={`contacts[${index}].lastName`}
                                  placeholder="Enter last name"
                                  value={contact.lastName || ''}
                                  onChange={handleInputChange}
                                  autoComplete="off"
                                />
                                {errors.contacts[index]?.lastName &&
                                  <div className="invalid-feedback">{errors.contacts[index].lastName}</div>
                                }
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="mb-3">
                                <label className="form-label">Email <span className="text-danger">*</span></label>
                                <input
                                  type="email"
                                  className={`form-control ${errors.contacts[index]?.email ? 'is-invalid' : ''}`}
                                  name={`contacts[${index}].email`}
                                  placeholder="Enter email address"
                                  value={contact.email || ''}
                                  onChange={handleInputChange}
                                  autoComplete="off"
                                />
                                {errors.contacts[index]?.email &&
                                  <div className="invalid-feedback">{errors.contacts[index].email}</div>
                                }
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="mb-3">
                                <label className="form-label">Contact Phone Number <span className="text-danger">*</span></label>
                                <input
                                  type="tel"
                                  className={`form-control ${errors.contacts[index]?.phone ? 'is-invalid' : ''}`}
                                  name={`contacts[${index}].phone`}
                                  placeholder="Enter contact phone number"
                                  value={contact.phone || ''}
                                  onChange={handleInputChange}
                                  autoComplete="off"
                                />
                                {errors.contacts[index]?.phone &&
                                  <div className="invalid-feedback">{errors.contacts[index].phone}</div>
                                }
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="mb-3">
                                <label className="form-label">Position <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.contacts[index]?.position ? 'is-invalid' : ''}`}
                                  name={`contacts[${index}].position`}
                                  placeholder="Enter position"
                                  value={contact.position || ''}
                                  onChange={handleInputChange}
                                  autoComplete="off"
                                />
                                {errors.contacts[index]?.position &&
                                  <div className="invalid-feedback">{errors.contacts[index].position}</div>
                                }
                              </div>
                            </div>

                            <div className="col-lg-6">
                              <div className="mb-3">
                                <label className="form-label">Allow Login Access</label>
                                <label className="form-check form-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={contact.allowLogin || false}
                                    onChange={(e) => handleContactLoginToggle(index, e.target.checked)}
                                  />
                                  <span className="form-check-label">Enable user account login</span>
                                </label>
                              </div>
                            </div>

                            {contact.allowLogin && (
                              <>
                                <div className="col-lg-6">
                                  <div className="mb-3">
                                    <label className="form-label">Generate Random Password</label>
                                    <label className="form-check form-switch">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={contact.generatePassword || false}
                                        onChange={(e) => handleGeneratePasswordToggle(index, e.target.checked)}
                                      />
                                      <span className="form-check-label">Auto-generate secure password</span>
                                    </label>
                                    {contact.generatePassword && (
                                      <div className="form-hint text-success">
                                        A secure password will be generated and sent to the user's email
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {!contact.generatePassword && (
                                  <>
                                    <div className="col-lg-6">
                                      <div className="mb-3">
                                        <label className="form-label">Password</label>
                                        <div className="input-group">
                                          <input
                                            type={showPassword ? 'text' : 'password'}
                                            className={`form-control ${errors.contacts[index]?.password ? 'is-invalid' : ''}`}
                                            name={`contacts[${index}].password`}
                                            value={contact.password || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter password"
                                            autoComplete="off"
                                          />
                                          <span className="input-group-text cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? (
                                              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye-off" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                <path d="M3 3l18 18"></path>
                                              </svg>
                                            ) : (
                                              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                              </svg>
                                            )}
                                          </span>
                                          {errors.contacts[index]?.password &&
                                            <div className="invalid-feedback">{errors.contacts[index].password}</div>
                                          }
                                        </div>
                                      </div>
                                    </div>

                                    <div className="col-lg-6">
                                      <div className="mb-3">
                                        <label className="form-label">Confirm Password</label>
                                        <div className="input-group">
                                          <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            className={`form-control ${errors.contacts[index]?.confirmPassword ? 'is-invalid' : ''}`}
                                            name={`contacts[${index}].confirmPassword`}
                                            value={contact.confirmPassword || ''}
                                            onChange={handleInputChange}
                                            placeholder="Confirm password"
                                          />
                                          <span className="input-group-text cursor-pointer" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                            {showConfirmPassword ? (
                                              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye-off" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                <path d="M3 3l18 18"></path>
                                              </svg>
                                            ) : (
                                              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                              </svg>
                                            )}
                                          </span>
                                          {errors.contacts[index]?.confirmPassword &&
                                            <div className="invalid-feedback">{errors.contacts[index].confirmPassword}</div>
                                          }
                                        </div>
                                        {contact.password && contact.confirmPassword && contact.password !== contact.confirmPassword && (
                                          <div className="invalid-feedback d-block">Passwords do not match</div>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </>
                            )}


                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="col-12 mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary d-flex align-items-center"
                      onClick={addContact}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                        strokeLinejoin="round" className="icon icon-1 me-1">
                        <path d="M12 5l0 14"></path>
                        <path d="M5 12l14 0"></path>
                      </svg>
                      Add Another Contact
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <a href="#" className="btn btn-link link-secondary"
                  data-bs-dismiss="modal">Cancel</a>
                <button type="submit" className="btn btn-primary ms-auto" id="createBtn" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Filter offcanvas */}
      <div className="offcanvas offcanvas-end" tabIndex="-1" id="offcanvasEnd" aria-labelledby="offcanvasEndLabel">
        <div className="offcanvas-header">
          <h2 className="offcanvas-title" id="offcanvasEndLabel">Filter</h2>
          <button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>
        <div className="offcanvas-body">
          <div className="accordion" id="accordion-filter">
            <div className="accordion-item">
              <h2 className="accordion-header" id="heading-1">
                <button className="accordion-button collapsed" type="button"
                  data-bs-toggle="collapse" data-bs-target="#collapse-createDate"
                  aria-expanded="false">
                  Created Date
                </button>
              </h2>
              <div id="collapse-createDate" className="accordion-collapse collapse"
                data-bs-parent="#accordion-filter">
                <div className="accordion-body pt-0">
                  <DateRangeFilter
                    fromDate={filterFromDate}
                    toDate={filterToDate}
                    onFromDateChange={setFilterFromDate}
                    onToDateChange={setFilterToDate}
                  />
                </div>
              </div>
            </div>
            {/* Additional filter sections would be added here */}
          </div>
        </div>
        <div className="offcanvas-footer">
          <div className="d-flex justify-content-end">
            <button type="button" className="btn btn-link link-secondary" onClick={handleResetFilter}>Reset</button>
            <button type="button" className="btn btn-primary me-2" onClick={handleApplyFilter}>Apply</button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmAction || (() => { })}
        title="Delete Confirmation"
        message="Are you sure you want to delete the selected items? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />

      {/* Add FailModal */}
      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />
    </>
  );
};

export default Companies; 
